// TEMPORARY: fake retail data for testing the UI with a live game.
// On with the dashboard-wide TEST_MODE (src/testMode.js), or by itself with DUMMY_ENABLED below.
// It only ever runs under `npm start` (development); production builds can never enable it.
// Remove this file (and its uses in RetailLocationController.js, Overview.js, LocationsController.js
// and the banner in RetailLocation.js) when done.
//
// Shares the test game (gates 2 hours, start 1 hour before page load, three past games). Sensors
// report every 30 seconds: entrance counts, queues and transaction times come back as 30-second
// readings; occupancy comes back one point per minute, as the occupancy route returns it.
// Each sensor steps through the edge cases below in 12-minute windows, on top of a realistic crowd.
import { getQueuesAPI } from '../../util.js';
import {
  IS_DEV, USE_TEST_MODE, noise, testSchedule, gameContext, readingTimes, localStamp, edgeCase, edgeProgress, crowdCurve,
} from '../../testMode';

const DUMMY_ENABLED = false;

// Overview test mode: every Overview section switched on for the store and 4 games picked
// automatically. Turns on all fake retail data too, so nothing comes from the real API.
const DUMMY_OVERVIEW_ENABLED = false;

export const USE_DUMMY_RETAIL_OVERVIEW = IS_DEV && DUMMY_OVERVIEW_ENABLED;
export const USE_DUMMY_RETAIL_DATA = IS_DEV && (DUMMY_ENABLED || DUMMY_OVERVIEW_ENABLED || USE_TEST_MODE);

// Overview test mode: give the store every sensor type the Overview can show
// (entrances, sales for conversion, occupancy with zones, queues), keeping what it already has
export const withAllOverviewFeatures = (loc) => {
  if (!USE_DUMMY_RETAIL_OVERVIEW || !loc) return loc;
  const zones = loc.zones && loc.zones.length
    ? loc.zones
    : [1, 2, 3, 4, 5, 6].map((id) => ({ id, label: `Zone ${id}` }));
  return {
    ...loc,
    maxOccupancy: loc.maxOccupancy || 150,
    zones,
    doorCounterSensorIds: (loc.doorCounterSensorIds || []).length ? loc.doorCounterSensorIds : [0, 1],
    doorCounterSensorSalesIds: (loc.doorCounterSensorSalesIds || []).length ? loc.doorCounterSensorSalesIds : [90],
    queueTimeSensorIds: (loc.queueTimeSensorIds || []).length ? loc.queueTimeSensorIds : [1],
  };
};

// ---------- Occupancy (one point per minute, per zone) ----------

// Store-wide occupancy cases (all zones together)
const STORE_CASES = ['normal', 'atCapacity', 'normal', 'overCapacity', 'normal', 'empty', 'surge'];
// Per-zone cases
const ZONE_CASES = ['normal', 'normal', 'zero', 'normal', 'hot'];

const storeShare = (ctx, m) => {
  const c = edgeCase(STORE_CASES, 0, m);
  if (c === 'atCapacity') return 1;
  if (c === 'overCapacity') return 1.04;
  if (c === 'empty') return 0.02;
  if (c === 'surge') return 0.7 + edgeProgress(m) * 0.025;
  return Math.min(0.95, 0.15 + crowdCurve(ctx, m) * 0.75) * ctx.scale;
};

const zoneReadings = (currentLocation, ctx, m) => {
  const zones = currentLocation.zones || [];
  const max = currentLocation.maxOccupancy || 60;
  const total = Math.max(0, Math.round(storeShare(ctx, m) * max * (0.95 + noise(m + ctx.seed) * 0.1)));
  const weights = zones.map((z, i) => {
    const c = edgeCase(ZONE_CASES, i, m);
    if (c === 'zero' || c === 'stale') return 0;
    return ((1 + 0.6 * Math.sin(m / 22 + i * 1.7)) + 0.3) * (0.7 + noise(m * 7 + i * 13 + ctx.seed) * 0.6) * (c === 'hot' ? 3 : 1);
  });
  const sum = weights.reduce((a, b) => a + b, 0) || 1;
  let assigned = 0;
  const last = weights.reduce((acc, w, i) => (w > 0 ? i : acc), -1);
  return zones.map((z, i) => {
    const c = edgeCase(ZONE_CASES, i, m);
    const v = weights[i] === 0 ? 0 : i === last ? Math.max(0, total - assigned) : Math.round((total * weights[i]) / sum);
    assigned += v;
    return { id: z.id, occupancy: v, active: c !== 'stale' };
  });
};

// 'get-retail-location-daily-data-v2': [{ zoneId: [{ time, occupancy }] }]
const buildOccupancy = (currentLocation, dateStr) => {
  const ctx = gameContext(currentLocation.timeZone, dateStr);
  const zones = currentLocation.zones || [];
  if (!ctx) return zones.map((z) => ({ [z.id]: [] }));
  const readings = Object.fromEntries(zones.map((z) => [z.id, []]));
  for (let m = ctx.gates - 150; m <= ctx.endMinute; m += 1) {
    zoneReadings(currentLocation, ctx, m).forEach((z) => {
      readings[z.id].push({ time: localStamp(dateStr, m * 60, false), occupancy: z.occupancy });
    });
  }
  return zones.map((z) => ({ [z.id]: readings[z.id] }));
};

// ---------- Entrance counts (30-second readings, running totals per sensor) ----------

const DOOR_CASES = ['normal', 'normal', 'quiet', 'rush', 'normal', 'stopped'];

// People entering per minute
const entryRate = (ctx, m) => {
  const c = edgeCase(DOOR_CASES, 0, m);
  if (c === 'stopped') return 0; // counter reports but nobody comes in
  const base = (0.4 + crowdCurve(ctx, m) * 4) * ctx.scale;
  if (c === 'quiet') return base * 0.15;
  if (c === 'rush') return base * 3;
  return base;
};

const doorIdsOf = (currentLocation) => {
  const sales = currentLocation.doorCounterSensorSalesIds || [];
  const stairs = currentLocation.doorCounterSensorStairsIds || [];
  const doors = (currentLocation.doorCounterSensorIds || []).filter((id) => !sales.includes(id) && !stairs.includes(id));
  return { doors: doors.length ? doors : [1], sales, stairs };
};

// 'get-location-daily-door-count-v2': [{ timestamp, <sensorId>: in, <sensorId>_out: out, count }]
const buildDoorCounts = (currentLocation, dateStr, excludedSensorIds = []) => {
  const ctx = gameContext(currentLocation.timeZone, dateStr);
  if (!ctx) return [];
  const { doors, sales, stairs } = doorIdsOf(currentLocation);
  const rows = [];
  let entries = 0;
  readingTimes(ctx, ctx.gates - 150).forEach((sec, k) => {
    if (k > 0) entries += (entryRate(ctx, Math.floor(sec / 60)) / 2) * (0.7 + noise(sec / 30 + ctx.seed) * 0.6);
    const total = Math.round(entries);
    const row = { timestamp: localStamp(dateStr, sec) };
    doors.forEach((id, i) => {
      const share = doors.length === 1 ? 1 : (i === 0 ? 0.55 : 0.45 / (doors.length - 1));
      row[id] = Math.round(total * share);
      row[`${id}_out`] = Math.round(total * share * 0.92);
    });
    sales.forEach((id, i) => {
      const share = (i === 0 ? 0.6 : 0.4 / Math.max(1, sales.length - 1)) * 0.3;
      row[id] = Math.round(total * share * 1.05);
      row[`${id}_out`] = Math.round(total * share);
    });
    stairs.forEach((id) => {
      row[id] = Math.round(total * 0.25);
      row[`${id}_out`] = Math.round(total * 0.2);
    });
    row.count = doors.concat(sales, stairs)
      .filter((id) => !excludedSensorIds.includes(id))
      .reduce((s, id) => s + (row[id] || 0), 0);
    rows.push(row);
  });
  // The views skip the first row as the zero baseline
  if (rows.length) Object.keys(rows[0]).forEach((key) => { if (key !== 'timestamp') rows[0][key] = 0; });
  return rows;
};

// ---------- Queues and transaction times (30-second readings) ----------

const QUEUE_CASES = ['normal', 'exact10', 'nine', 'spike', 'minimum', 'rising', 'falling'];

const queueIds = (currentLocation) =>
  (currentLocation.queueZone?.shapes || []).map((s) => s.id).concat(
    currentLocation.queueZone?.shapes?.length ? [] : (currentLocation.queueTimeSensorIds || [1])
  );

const transactionIds = (currentLocation) =>
  currentLocation.transactionZone?.shapes?.length ? currentLocation.transactionZone.shapes.map((s) => s.id) : [1, 2];

// One line's queue at a reading, or null while that line's sensor is stale
const queueReading = (ctx, sec, i) => {
  const m = Math.floor(sec / 60);
  const c = edgeCase(QUEUE_CASES, i, m);
  if (c === 'stale') return null;
  const p = edgeProgress(m);
  const n = noise(sec / 30 + i * 31 + ctx.seed);
  const base = 1 + crowdCurve(ctx, m) * 9 * (1 + i * 0.3) * ctx.scale + n * 2;
  const v = {
    exact10: 10, nine: 9, spike: 22 + n * 6, minimum: 1, rising: 3 + p * 1.2, falling: 16 - p * 1.2,
  }[c] ?? base;
  return Math.max(1, Math.round(v));
};

// 'get-retail-location-daily-queue-time': [{ timestamp, queueTime: { id: min }, peopleCount: { id: n } }]
const buildQueue = (currentLocation, dateStr) => {
  const ctx = gameContext(currentLocation.timeZone, dateStr);
  if (!ctx) return [];
  const ids = queueIds(currentLocation);
  const rows = [];
  readingTimes(ctx, ctx.gates - 30).forEach((sec) => {
    const queueTime = {};
    const peopleCount = {};
    ids.forEach((id, i) => {
      const v = queueReading(ctx, sec, i);
      if (v == null) return;
      queueTime[id] = v;
      peopleCount[id] = Math.round(v * 2);
    });
    if (Object.keys(queueTime).length) rows.push({ timestamp: localStamp(dateStr, sec), queueTime, peopleCount });
  });
  return rows;
};

// 'get-retail-location-daily-transaction-time': [{ timestamp, transactionTime: { id: seconds } }]
const buildTransactions = (currentLocation, dateStr) => {
  const ctx = gameContext(currentLocation.timeZone, dateStr);
  if (!ctx) return [];
  const ids = transactionIds(currentLocation);
  const rows = [];
  readingTimes(ctx, ctx.gates - 30).forEach((sec) => {
    const m = Math.floor(sec / 60);
    const transactionTime = {};
    ids.forEach((id, i) => {
      const c = edgeCase(QUEUE_CASES, i + 3, m);
      const n = noise(sec / 30 + i * 17 + ctx.seed);
      // 20 seconds quick to 6+ minutes slow
      transactionTime[id] = Math.round(c === 'spike' ? 330 + n * 60 : c === 'minimum' ? 20 + n * 10 : 60 + i * 25 + crowdCurve(ctx, m) * 70 + n * 30);
    });
    rows.push({ timestamp: localStamp(dateStr, sec), transactionTime });
  });
  return rows;
};

// ---------- Locations page card (current data) ----------

const liveContext = (currentLocation) =>
  gameContext(currentLocation.timeZone, testSchedule(currentLocation.timeZone)[1].dates[0].date);

// 'get-retail-location-current-data-dashboard-v2': [{ zoneId, occupancy, sensorActive }]
const buildCurrentOccupancy = (currentLocation) => {
  const ctx = liveContext(currentLocation);
  return zoneReadings(currentLocation, ctx, ctx.endMinute).map((z) => ({
    zoneId: z.id,
    occupancy: z.active ? z.occupancy : 0,
    sensorActive: z.active,
  }));
};

// 'get-retail-location-current-count-data-dashboard': { currentDoorCount }
const buildCurrentCount = (currentLocation, excludedSensorIds = []) => {
  const ctx = liveContext(currentLocation);
  const rows = buildDoorCounts(currentLocation, ctx.date, excludedSensorIds);
  return { currentDoorCount: rows.length ? rows[rows.length - 1].count : 0 };
};

// 'get-retail-location-current-queue-time': { queueTime, peopleCount } (first line; 0 while stale)
const buildCurrentQueue = (currentLocation) => {
  const ctx = liveContext(currentLocation);
  const sec = ctx.endSec - (ctx.endSec % 30);
  const v = queueReading(ctx, sec, 0);
  return { queueTime: v == null ? 0 : Math.round(v), peopleCount: v == null ? 0 : Math.round(v * 2) };
};

// Drop-in replacement for getQueuesAPI. Serves fake schedule and data, defers everything else.
export const getRetailAPI = (currentLocation) => (route, body, callback, token) => {
  if (!USE_DUMMY_RETAIL_DATA || !currentLocation) return getQueuesAPI(route, body, callback, token);
  const respond = (data) => setTimeout(() => callback(data), 150);
  switch (route) {
    case 'get-location-current-schedule':
      return respond(testSchedule(currentLocation.timeZone));
    case 'get-retail-location-daily-data-v2':
      return respond(buildOccupancy(currentLocation, body.date));
    case 'get-location-daily-door-count-v2':
      return respond(buildDoorCounts(currentLocation, body.date, body.excludedSensorIds || []));
    case 'get-retail-location-daily-queue-time':
      return respond(buildQueue(currentLocation, body.date));
    case 'get-retail-location-daily-transaction-time':
      return respond(buildTransactions(currentLocation, body.date));
    case 'get-retail-location-zone-labels':
      return respond({});
    case 'get-retail-location-current-data-dashboard-v2':
      return respond(buildCurrentOccupancy(currentLocation));
    case 'get-retail-location-current-count-data-dashboard': {
      // The card asks for entrance sensors only; exclude everything else
      const asked = body.doorCountSensorIds || [];
      const { doors, sales, stairs } = doorIdsOf(currentLocation);
      const excluded = doors.concat(sales, stairs).filter((id) => asked.length && !asked.includes(id));
      return respond(buildCurrentCount(currentLocation, excluded));
    }
    case 'get-retail-location-current-queue-time':
      return respond(buildCurrentQueue(currentLocation));
    default:
      return getQueuesAPI(route, body, callback, token);
  }
};
